import {useEffect, useRef, useState} from 'react';
import styles from './SleepFragments.module.css';

const EVENT_POSITIONS: ReadonlyArray<{cx: number; cy: number}> = [
  {cx: 22, cy: 68},
  {cx: 32, cy: 58},
  {cx: 42, cy: 74},
  {cx: 52, cy: 62},
  {cx: 62, cy: 80},
  {cx: 72, cy: 86},
  {cx: 82, cy: 76},
  {cx: 92, cy: 72},
  {cx: 102, cy: 64},
  {cx: 112, cy: 58},
  {cx: 122, cy: 62},
  {cx: 132, cy: 70},
  {cx: 142, cy: 64},
  {cx: 152, cy: 68},
  {cx: 162, cy: 72},
  {cx: 172, cy: 80},
  {cx: 182, cy: 84},
  {cx: 192, cy: 78},
  {cx: 202, cy: 72},
  {cx: 212, cy: 66},
  {cx: 222, cy: 60},
  {cx: 232, cy: 66},
  {cx: 242, cy: 72},
  {cx: 252, cy: 66},
  {cx: 262, cy: 62},
  {cx: 272, cy: 68},
  {cx: 282, cy: 74},
  {cx: 292, cy: 80},
  {cx: 302, cy: 76},
];

const CYCLE_MS = 7000;
const COUNT_UP_MS = 2300;
const FADE_END_MS = 6650;
const FINAL_VALUE = 280;
const START_DELAY_MS = 2000;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export const SleepFragments = () => {
  const [display, setDisplay] = useState('0');
  const rafRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (prefersReducedMotion()) {
      setDisplay('280+');
      return;
    }

    const animStart = performance.now() + START_DELAY_MS;

    const tick = (now: number) => {
      const elapsed = now - animStart;
      if (elapsed < 0) {
        setDisplay('0');
      } else {
        const t = elapsed % CYCLE_MS;
        if (t < COUNT_UP_MS) {
          const progress = t / COUNT_UP_MS;
          const eased = 1 - Math.pow(1 - progress, 2.5);
          const value = Math.floor(eased * FINAL_VALUE);
          setDisplay(value + (progress > 0.95 ? '+' : ''));
        } else if (t < FADE_END_MS) {
          setDisplay(`${FINAL_VALUE}+`);
        } else {
          setDisplay('0');
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <figure className={styles.figure} aria-label="One typical night with frequent breathing interruptions">
      <div className={styles.label}>One typical night · 8 hours</div>
      <svg viewBox="0 0 320 130" className={styles.svg} preserveAspectRatio="xMidYMid meet" aria-hidden="true">
        <text x="14" y="118" className={styles.timeLabel}>
          11pm
        </text>
        <text x="160" y="118" textAnchor="middle" className={styles.timeLabel}>
          3am
        </text>
        <text x="306" y="118" textAnchor="end" className={styles.timeLabel}>
          7am
        </text>

        <line x1="14" y1="60" x2="306" y2="60" stroke="var(--osa-divider)" strokeWidth="1" />
        <line x1="14" y1="100" x2="306" y2="100" stroke="var(--osa-divider)" strokeWidth="1" strokeDasharray="2 4" />

        <path
          className={styles.sleepLine}
          d="M14 70 C 40 50, 60 90, 90 75 S 130 55, 160 70 S 200 90, 230 70 S 270 55, 306 75"
          fill="none"
          stroke="var(--osa-text-muted)"
          strokeWidth="1.8"
          strokeLinecap="round"
          opacity="0.7"
        />

        <g>
          {EVENT_POSITIONS.map(({cx, cy}, i) => (
            <g key={`${cx}-${cy}`} className={styles.eventMark} style={{animationDelay: `${2000 + i * 80}ms`}}>
              <circle cx={cx} cy={cy} r="3" fill="none" stroke="var(--osa-accent-alarm)" strokeWidth="1.2" />
              <circle cx={cx} cy={cy} r="1" fill="var(--osa-accent-alarm)" />
            </g>
          ))}
        </g>
      </svg>

      <div className={styles.footer}>
        <div>
          <span className={styles.statValue}>{display}</span>
          <span className={styles.statSuffix}>interruptions</span>
        </div>
        <span className={styles.statLabel}>~1 every 1.7 min</span>
      </div>
    </figure>
  );
};

export default SleepFragments;
